import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { CheckCircle2, AlertCircle, Clock3 } from 'lucide-react';

function getApiUrl() {
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL as string;
  if (window.location.hostname !== 'localhost') return `${window.location.origin}/api`;
  return '/api';
}

type AckData = {
  id: number;
  uid: string;
  target_type: string;
  target_id: number;
  acknowledger_kind: string;
  acknowledger_ref: string;
  token_expires_at: string | null;
  acknowledged_at: string | null;
  tu_title?: string | null;
  tu_topic?: string | null;
  tu_homework_html?: string | null;
  tu_start_at?: string | null;
  tu_end_at?: string | null;
};

/**
 * Öffentliche, token-basierte Kenntnisnahme-Seite für Eltern.
 * Analog zur ElternbriefPublic, aber schlank.
 */
export default function KlassenbuchAckPublic() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<AckData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const sanitizedHomeworkHtml = data?.tu_homework_html ? DOMPurify.sanitize(data.tu_homework_html) : '';

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${getApiUrl()}/klassenbuch-public/acknowledgments/${token}`)
      .then((res) => setData(res.data.data))
      .catch((err) => {
        const code = err?.response?.data?.code;
        if (code === 'TOKEN_NOT_FOUND') setError('Dieser Link ist ungültig.');
        else if (code === 'TOKEN_EXPIRED') setError('Dieser Link ist abgelaufen.');
        else setError(err?.response?.data?.error || 'Fehler beim Laden');
      });
  }, [token]);

  async function confirm() {
    if (!token) return;
    setSaving(true);
    try {
      const res = await axios.post(`${getApiUrl()}/klassenbuch-public/acknowledgments/${token}/confirm`, {});
      setData(res.data.data);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error || 'Bestätigung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <div className="max-w-md w-full bg-white border border-rose-200 rounded-lg p-6 text-center">
          <AlertCircle className="text-rose-500 mx-auto" size={40} />
          <h1 className="mt-2 text-lg font-semibold text-gray-900">Zugriff nicht möglich</h1>
          <p className="mt-1 text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500 text-sm">Lade…</div>
      </div>
    );
  }

  const already = !!data.acknowledged_at;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <header className="px-6 py-4 bg-blue-50 border-b border-blue-100">
          <h1 className="text-lg font-semibold text-blue-900">Klassenbuch – Kenntnisnahme</h1>
          <p className="text-sm text-blue-800">
            Bitte bestätigen Sie, dass Sie die folgenden Informationen zur Kenntnis genommen haben.
          </p>
        </header>
        <div className="p-6 space-y-4 text-sm text-gray-800">
          {data.tu_title && (
            <div>
              <div className="text-xs text-gray-500">Stunde</div>
              <div className="font-medium text-gray-900">{data.tu_title}</div>
              {data.tu_start_at && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {new Date(data.tu_start_at).toLocaleString('de-DE')}
                </div>
              )}
            </div>
          )}
          {data.tu_topic && (
            <div>
              <div className="text-xs text-gray-500">Thema</div>
              <div>{data.tu_topic}</div>
            </div>
          )}
          {sanitizedHomeworkHtml && (
            <div>
              <div className="text-xs text-gray-500">Hausaufgaben</div>
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizedHomeworkHtml }}
              />
            </div>
          )}

          {already ? (
            <div className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 flex items-center gap-2">
              <CheckCircle2 className="text-emerald-600" size={18} />
              <div>
                <div className="text-sm text-emerald-900 font-medium">
                  Kenntnisnahme bestätigt
                </div>
                <div className="text-xs text-emerald-800">
                  am {new Date(data.acknowledged_at as string).toLocaleString('de-DE')}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2">
                <Clock3 className="text-amber-600 mt-0.5" size={18} />
                <div className="text-xs text-amber-900">
                  Mit dem Klick auf „Bestätigen“ wird Ihre Kenntnisnahme mit Datum und IP-Adresse im
                  Klassenbuch dokumentiert.
                </div>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => void confirm()}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                <CheckCircle2 size={16} />
                {saving ? 'Bestätige…' : 'Kenntnisnahme bestätigen'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
